import React, { useState, useEffect } from 'react';
import * as Sentry from '@sentry/react';

import { Button, Card, CardHeader, CardBody, CardFooter, Form, Input, InputGroupText, InputGroup } from 'reactstrap';
import toast from 'react-hot-toast';

import { frontend } from '../../../api';
import { validateInt } from '../../../validator';
import { FaKey, FaSpinner } from 'react-icons/fa';
import { UnauthorizedException } from '../../../api/exceptions/UnauthorizedException';
import { InternalServerErrorException } from '../../../api/exceptions/InternalServerErrorException';

interface Props {
	data: { email?: string; dob?: string; firstName?: string; lastName?: string; loginToken?: string; verificationCodeEmail?: string };
	signIn: (data: { emailAddress: string; dob: string; firstName: string; lastName: string; organization: string; token?: string }) => void;
}

export const GuestVerify = (props: Props) => {
	const { signIn, data: userData } = props;
	const [checkButtonEnabled, setCheckButtonEnabled] = useState(false);
	const [isSubmitInProgress, setIsSubmitInProgress] = useState(false);
	const [data, setData] = useState<{ verificationCode?: string }>({});

	useEffect(() => {
		let canSave = true;

		if (data.verificationCode == null || data.verificationCode === '') {
			canSave = false;
		}

		if (!validateInt(data.verificationCode!)) {
			canSave = false;
		}

		setCheckButtonEnabled(canSave);
	}, [data]);

	const handleInputFieldChange = (fieldName: 'verificationCode', newValue: any) => {
		const newData = { ...data };
		newData[fieldName] = newValue;
		setData(newData);
	};

	const handleVerifyClick = async (e: React.MouseEvent<HTMLButtonElement, MouseEvent> | React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();

		if (userData.loginToken == null) {
			toast.error('There was an error processing the login. Please try again.');
			return;
		}

		setIsSubmitInProgress(true);

		try {
			const result = await frontend.guestValidate(userData.loginToken!, data.verificationCode!);

			signIn({ ...result });
		} catch (error) {
			if (error instanceof UnauthorizedException) {
				toast.error(error.message);
			} else if (error instanceof InternalServerErrorException) {
				toast.error('Service currently unavailable');
			} else {
				console.error(error);
				Sentry.captureException(error);
			}
		} finally {
			setIsSubmitInProgress(false);
		}
	};

	return (
		<Form action="" className="form" method="" onSubmit={(e) => handleVerifyClick(e)}>
			<Card className="card-login">
				<CardHeader>
					<CardHeader>
						<h3 className="header text-center">Verification</h3>
					</CardHeader>
				</CardHeader>
				<CardBody>
					Please check the email associated with the account {userData != null ? `(${userData.verificationCodeEmail})` : ''} and enter the
					verification code below.
					<InputGroup>
						<InputGroupText style={{ background: 'none' }}>
							<FaKey color="#66615b" />
						</InputGroupText>
						<Input
							type="number"
							name={'verificationCode'}
							id={'verificationCode'}
							autoFocus
							placeholder="Verification Code"
							onChange={(e) => handleInputFieldChange('verificationCode', e.target.value)}
						/>
					</InputGroup>
					If you don't have access to this account, contact {process.env.REACT_APP_CLIENT_SUPORT_EMAIL}
				</CardBody>
				<CardFooter>
					<Button
						block
						className="btn-round mb-3"
						color="primary"
						type="button"
						disabled={isSubmitInProgress || (!isSubmitInProgress && !checkButtonEnabled)}
						onClick={(e) => handleVerifyClick(e)}
					>
						{isSubmitInProgress ? <FaSpinner size={12} className="fa-spin" /> : 'Verify'}
					</Button>
				</CardFooter>
			</Card>
		</Form>
	);
};
